import { FC, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ContentLayout,
  Header,
  Container,
  SpaceBetween,
  Box,
  Spinner,
  Table,
  ColumnLayout,
  StatusIndicator,
  Badge,
  ExpandableSection,
  Select,
  SelectProps,
  FormField,
} from '@amzn/awsui-components-react';
import { useGetSessionReviewQuery } from 'src/store/apis/uatApi';
import { UatQueryDetail } from 'src/store/interfaces/Uat.types';
import ToolCallCard from './ToolCallCard';
import RawResponseView from './RawResponseView';

// --- Helpers ---

type VoteResult = 'correct' | 'incorrect' | 'pending';

const getQueryVote = (q: UatQueryDetail): VoteResult => {
  if (!q.feedback) return 'pending';
  const votes = q.feedback.toolFeedbacks.map((f) => f.vote);
  if (votes.some((v) => v === 'down')) return 'incorrect';
  if (votes.length > 0 && votes.every((v) => v === 'up')) return 'correct';
  return 'pending';
};

const VOTE_INDICATOR: Record<VoteResult, { type: 'success' | 'error' | 'stopped'; label: string }> = {
  correct: { type: 'success', label: 'Correct' },
  incorrect: { type: 'error', label: 'Incorrect' },
  pending: { type: 'stopped', label: 'Pending' },
};

const pct = (n: number, total: number): string => (total === 0 ? '0' : ((n / total) * 100).toFixed(0));

const ALL_OPTION: SelectProps.Option = { value: '__all__', label: 'All' };

// --- Component ---

const UatSessionReview: FC = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { data: session, isLoading } = useGetSessionReviewQuery(sessionId!, { skip: !sessionId });
  const [userFilter, setUserFilter] = useState<SelectProps.Option>(ALL_OPTION);
  const [voteFilter, setVoteFilter] = useState<SelectProps.Option>(ALL_OPTION);

  const metrics = useMemo(() => {
    if (!session) return null;
    let correct = 0;
    let incorrect = 0;
    let pending = 0;
    const byUser: Record<string, { queries: number; feedbacks: number; correct: number; incorrect: number }> = {};

    for (const q of session.queries) {
      const u = (byUser[q.userId] ??= { queries: 0, feedbacks: 0, correct: 0, incorrect: 0 });
      u.queries++;
      const vote = getQueryVote(q);
      if (vote === 'correct') {
        correct++;
        u.feedbacks++;
        u.correct++;
      } else if (vote === 'incorrect') {
        incorrect++;
        u.feedbacks++;
        u.incorrect++;
      } else {
        pending++;
        if (q.feedback) u.feedbacks++;
      }
    }

    return { total: session.queries.length, correct, incorrect, pending, byUser };
  }, [session]);

  const userOptions = useMemo<SelectProps.Option[]>(() => {
    if (!metrics) return [ALL_OPTION];
    return [ALL_OPTION, ...Object.keys(metrics.byUser).sort().map((u) => ({ value: u, label: u }))];
  }, [metrics]);

  const voteOptions: SelectProps.Option[] = [
    ALL_OPTION,
    { value: 'correct', label: 'Correct' },
    { value: 'incorrect', label: 'Incorrect' },
    { value: 'pending', label: 'Pending' },
  ];

  const filteredQueries = useMemo(() => {
    if (!session) return [];
    return session.queries.filter((q) => {
      if (userFilter.value !== '__all__' && q.userId !== userFilter.value) return false;
      if (voteFilter.value !== '__all__' && getQueryVote(q) !== voteFilter.value) return false;
      return true;
    });
  }, [session, userFilter, voteFilter]);

  if (isLoading || !session || !metrics) {
    return <Spinner size="large" />;
  }

  return (
    <ContentLayout
      header={
        <Header
          variant="h1"
          description={`Created by ${session.createdBy} · ${new Date(session.createdTs).toLocaleString()}`}
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Box>
                <span
                  style={{ color: '#0972d3', cursor: 'pointer', textDecoration: 'underline' }}
                  onClick={() => navigate(`/uat/sessions/${sessionId}`)}
                >
                  Back to Session
                </span>
              </Box>
            </SpaceBetween>
          }
        >
          Review: {session.sessionName}
        </Header>
      }
    >
      <SpaceBetween size="l">
        {/* Metrics Cards */}
        <Container header={<Header variant="h3">Overview</Header>}>
          <ColumnLayout columns={4} variant="text-grid">
            <div>
              <Box fontSize="display-l" fontWeight="bold">
                {metrics.total}
              </Box>
              <Box color="text-body-secondary">Total Queries</Box>
            </div>
            <div>
              <Box fontSize="display-l" fontWeight="bold" color="text-status-success">
                {metrics.correct}
              </Box>
              <Box color="text-body-secondary">Correct ({pct(metrics.correct, metrics.total)}%)</Box>
            </div>
            <div>
              <Box fontSize="display-l" fontWeight="bold" color="text-status-error">
                {metrics.incorrect}
              </Box>
              <Box color="text-body-secondary">Incorrect ({pct(metrics.incorrect, metrics.total)}%)</Box>
            </div>
            <div>
              <Box fontSize="display-l" fontWeight="bold">
                {metrics.pending}
              </Box>
              <Box color="text-body-secondary">Pending ({pct(metrics.pending, metrics.total)}%)</Box>
            </div>
          </ColumnLayout>
        </Container>

        {/* Per-User Breakdown */}
        <Container header={<Header variant="h3">Per-User Breakdown</Header>}>
          <Table
            items={Object.entries(metrics.byUser)
              .map(([user, m]) => ({ user, ...m }))
              .sort((a, b) => a.user.localeCompare(b.user))}
            columnDefinitions={[
              { id: 'user', header: 'User', cell: (item) => item.user },
              { id: 'queries', header: 'Queries', cell: (item) => item.queries },
              { id: 'feedbacks', header: 'Feedbacks', cell: (item) => item.feedbacks },
              {
                id: 'correct',
                header: 'Correct',
                cell: (item) => (
                  <StatusIndicator type="success">{item.correct}</StatusIndicator>
                ),
              },
              {
                id: 'incorrect',
                header: 'Incorrect',
                cell: (item) => (
                  <StatusIndicator type="error">{item.incorrect}</StatusIndicator>
                ),
              },
            ]}
            variant="embedded"
            empty={<Box textAlign="center">No data</Box>}
          />
        </Container>

        {/* All Queries */}
        <Container
          header={
            <Header
              variant="h3"
              actions={
                <SpaceBetween direction="horizontal" size="s">
                  <FormField label="User">
                    <Select
                      selectedOption={userFilter}
                      onChange={({ detail }) => setUserFilter(detail.selectedOption)}
                      options={userOptions}
                    />
                  </FormField>
                  <FormField label="Feedback Status">
                    <Select
                      selectedOption={voteFilter}
                      onChange={({ detail }) => setVoteFilter(detail.selectedOption)}
                      options={voteOptions}
                    />
                  </FormField>
                </SpaceBetween>
              }
            >
              All Queries ({filteredQueries.length})
            </Header>
          }
        >
          <SpaceBetween size="m">
            {filteredQueries.length === 0 && (
              <Box textAlign="center" color="text-body-secondary" padding="l">
                No queries match the current filters.
              </Box>
            )}
            {filteredQueries.map((q, idx) => {
              const vote = getQueryVote(q);
              const indicator = VOTE_INDICATOR[vote];
              const queryStatusType = q.status === 'COMPLETED' ? 'success' : q.status === 'FAILED' ? 'error' : 'in-progress';
              return (
                <ExpandableSection
                  key={q.queryId}
                  headerText={`${idx + 1}. ${q.userQuery}`}
                  headerDescription={`${q.userId} · ${q.status}${q.durationMs != null ? ` · ${(q.durationMs / 1000).toFixed(1)}s` : ''} · ${q.actualTools.length} tool(s) · ${new Date(q.submittedTs).toLocaleString()}`}
                  headerActions={
                    <StatusIndicator type={indicator.type}>Feedback: {indicator.label}</StatusIndicator>
                  }
                >
                  <SpaceBetween size="s">
                    {/* Raw Response */}
                    {q.rawResponse && (
                      <SpaceBetween size="xs">
                        <Box padding={{ left: 'l' }}>
                          <SpaceBetween direction="horizontal" size="s">
                            <Box fontSize="heading-s" fontWeight="bold">Raw Response (AskRino)</Box>
                            {q.feedback?.rawResponseVote && (
                              <StatusIndicator type={q.feedback.rawResponseVote === 'up' ? 'success' : 'error'}>
                                {q.feedback.rawResponseVote === 'up' ? 'Correct' : 'Incorrect'}
                              </StatusIndicator>
                            )}
                          </SpaceBetween>
                        </Box>
                        <ExpandableSection
                          headerText={<Box fontSize="body-s">View Response</Box>}
                          defaultExpanded
                        >
                          <RawResponseView content={q.rawResponse} />
                        </ExpandableSection>
                      </SpaceBetween>
                    )}

                    {/* Tool Cards (read-only) */}
                    {q.actualTools.length > 0 ? (
                      q.actualTools.map((tool, i) => {
                        const fb = q.feedback?.toolFeedbacks[i] ?? {
                          toolIndex: i,
                          toolName: tool.toolName,
                          vote: null,
                          correctedParameters: null,
                        };
                        return <ToolCallCard key={i} toolCall={tool} feedback={fb} onFeedbackChange={() => {}} editing={false} />;
                      })
                    ) : q.feedback?.toolFeedbacks[0] ? (
                      <ToolCallCard
                        toolCall={{ toolName: 'No Tool', toolParameters: {}, status: 'N/A', responseData: null, durationMs: null }}
                        feedback={q.feedback.toolFeedbacks[0]}
                        onFeedbackChange={() => {}}
                        editing={false}
                      />
                    ) : (
                      <Box color="text-body-secondary">No tool calls.</Box>
                    )}

                    {/* Feedback comment */}
                    {q.feedback?.comments && (
                      <Box fontSize="body-s" color="text-body-secondary">
                        Comment: {q.feedback.comments} — {q.feedback.submittedBy},{' '}
                        {new Date(q.feedback.submittedTs).toLocaleString()}
                      </Box>
                    )}
                  </SpaceBetween>
                </ExpandableSection>
              );
            })}
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </ContentLayout>
  );
};

export default UatSessionReview;
